import { useEffect, useRef, type RefObject } from "react";
import { Trash2 } from "lucide-react";
import { Button, Icon, useDismiss } from "@cntrl/ui";
import { sanitizeRoute } from "@cntrl/native";
import {
  useNotifications,
  useMarkRead,
  useMarkAllRead,
  useDismiss as useDismissNotification,
  useDismissAll,
  type NotificationRow,
} from "../../app/hooks/useNotifications.js";
import { APP_ROUTE_ROOTS } from "../../app/deepLink.js";
import { useOpenNotificationRoute } from "../../app/notificationBand.js";
import { NotificationList } from "./NotificationList.js";
import styles from "./NotificationMenu.module.scss";

export interface NotificationMenuProps {
  onClose: () => void;
  /** Navigate to a notification's deep-link (already same-origin-sanitized). Omitted ⇒ tapping only reads. */
  onNavigate?: (route: string) => void;
  /** Open the full notification page — the "See all" affordance. */
  onSeeAll?: () => void;
  /**
   * The box that counts as "inside" for the outside-press dismiss — pass the element wrapping the TRIGGER
   * and this panel. Defaults to the panel alone, which closes on the bell's pointerdown and lets the bell's
   * click open it straight back up: the bell could open the panel and never close it (ART-356).
   */
  dismissBoundaryRef?: RefObject<HTMLElement | null>;
}

/**
 * The bell's panel on DESKTOP: a dropdown anchored under the bell (mobile routes to the full
 * `/notifications` page instead and never mounts this).
 *
 * It was a full-height right-side drawer — a modal dialog with a scrim and a focus trap — until
 * 2026‑08‑17. A notification list is a **light** surface: you glance, maybe click through, and leave. A
 * modal made a glance cost a scrim over the whole app and a trap you had to Escape out of, and it broke
 * the anchoring the design calls for. So this is now the same light-dismiss popup as the nav dropdowns
 * and the band switcher: `useDismiss` (outside pointerdown + Escape), no scrim, **no trap** — trapping
 * focus in a surface the user did not choose to enter is the wrong contract, and it is why the drawer
 * needed all that bespoke Tab-cycling code.
 *
 * It keeps `role="dialog"` (a labelled surface, so a screen reader announces what opened) but deliberately
 * NOT `aria-modal`, which would claim the rest of the page is inert when it is not.
 */
export function NotificationMenu({ onClose, onNavigate, onSeeAll, dismissBoundaryRef }: NotificationMenuProps) {
  const list = useNotifications();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const dismissNotification = useDismissNotification();
  const dismissAll = useDismissAll();

  const panelRef = useRef<HTMLDivElement>(null);
  useDismiss(true, onClose, dismissBoundaryRef ?? panelRef);

  // Focus moves INTO the panel on open and back to the bell on close — the keyboard affordance a popup
  // owes you — but nothing is trapped: Tab walks out into the page, which is what non-modal means.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLElement>("button:not([disabled]), [href]")?.focus();
    return () => previouslyFocused?.focus();
  }, []);

  const items = list.data?.items ?? [];
  const openRoute = useOpenNotificationRoute();

  function openRow(n: NotificationRow) {
    if (!n.read && n.id) markRead.mutate(n.id);
    if (n.route) {
      // Same-origin AND route-root allowlisted (defense-in-depth parity with the push/universal-link tap path,
      // which routes through the same APP_ROUTE_ROOTS): an off-list or cross-origin route is dropped, never navigated.
      const safe = sanitizeRoute(n.route, APP_ROUTE_ROOTS);
      if (safe && onNavigate) {
        // A row for another of the person's bands asks before switching to it (notificationBand.ts).
        openRoute(safe, n.bandId, onNavigate);
        onClose();
      }
    }
  }

  return (
    <div
      className={styles.panel}
      role="dialog"
      aria-labelledby="notif-menu-title"
      ref={panelRef}
    >
      <div className={styles.header}>
        <h2 id="notif-menu-title" className={styles.title}>
          Notifications
        </h2>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => markAllRead.mutate()}
          disabled={items.length === 0}
        >
          Mark all read
        </Button>
      </div>

      <div className={styles.body}>
        <NotificationList
          items={items}
          isLoading={list.isLoading}
          isError={list.isError}
          error={list.error}
          onRetry={() => void list.refetch()}
          onOpen={openRow}
          onDismiss={(id) => dismissNotification.mutate(id)}
        />
      </div>

      {items.length > 0 && (
        <div className={styles.footer}>
          {onSeeAll && (
            <Button variant="ghost" size="xs" onClick={onSeeAll}>
              See all
            </Button>
          )}
          <Button variant="ghost" size="xs" onClick={() => dismissAll.mutate()}>
            <Icon icon={Trash2} size={16} /> Clear all
          </Button>
        </div>
      )}
    </div>
  );
}
